import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import React, { useCallback, useEffect, useState } from 'react';
import { isNil, noop } from 'lodash-es';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { IGetRestaurantResponse } from './proto/restaurant/restaurant';
import { Menu } from './Menu/Menu';
import { setSelectedRestaurantID, setToken } from './store/auth/authSlice';
import { useAppDispatch } from './store/hooks';
import { selectSelectedRestaurantId, selectToken } from './store/auth/selectors';
import { ModalProvider } from './providers/ModalProvider/ModalProvider';
import { RestaurantManagement } from './RestaurantManagement/RestaurantManagement';
import { TopMenu } from './Menu/TopMenu/TopMenu';
import { Home } from './Home/Home';
import { Orders } from './Orders/Orders';
import { useRestaurantSse } from './utils/restaurantSse/restaurantSse.hook';

interface IRestaurantContext {
	restaurants: IGetRestaurantResponse[];
	selectedRestaurantId: string | null;
	setSelectedRestaurantId: (id: number) => void;
	dropdownHidden: boolean;
	setDropdownHidden: (hide: boolean) => void;
	isDropdownDisabled: boolean;
	setIsDropdownDisabled: (disabled: boolean) => void;
	userToken: string;
}

export const RestaurantContext = React.createContext<IRestaurantContext>({
	restaurants: [],
	selectedRestaurantId: null,
	setSelectedRestaurantId: noop,
	dropdownHidden: false,
	setDropdownHidden: noop,
	isDropdownDisabled: false,
	setIsDropdownDisabled: noop,
	userToken: null,
});

export const AdminWrapper = (): JSX.Element => {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const restaurantId = useSelector(selectSelectedRestaurantId);
	const stateToken = useSelector(selectToken);
	const dispatch = useAppDispatch();
	const [auth0Token, setAuth0Token] = useState<string>(null);
	const { logout, getAccessTokenSilently } = useAuth0();

	useRestaurantSse();

	const initAdmin = useCallback(async (): Promise<void> => {
		const token = await getAccessTokenSilently();

		// Set local token and store token to allow the final display only when they match
		dispatch(setToken(token));
		setAuth0Token(token);

		fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/api/restaurant`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
			.then((response) => response.json())
			.then((res) => {
				dispatch(setSelectedRestaurantID(res[0]?.id));
			})
			.catch((e) => {
				console.log(e);
			});

		setIsAuthenticated(true);
	}, [dispatch, getAccessTokenSilently]);

	useEffect(() => {
		initAdmin();
	}, [initAdmin]);

	if (!isAuthenticated) {
		return <div onClick={(): void => logout()}>Loading1...</div>;
	}

	if (isNil(restaurantId)) {
		return <div onClick={(): void => logout()}>loading2...</div>;
	}

	if (isNil(stateToken) || isNil(auth0Token) || stateToken !== auth0Token) {
		return <div onClick={(): void => logout()}>loading3...</div>;
	}

	return (
		<>
			<ToastContainer
				position="bottom-center"
				autoClose={3000}
				hideProgressBar={false}
				className="toast-container-fullwidth"
				newestOnTop
				closeOnClick
				pauseOnHover
			/>
			<MantineProvider>
				<ModalProvider>
					<TopMenu />
					<Routes>
						<Route path="/" element={<Navigate to="/home" replace />} />
						<Route path="home" element={<Home />} />
						<Route path="restaurantManagement/*" element={<RestaurantManagement />} />
						<Route path="menu/:menuId" element={<Menu />} />
						<Route path="orders" element={<Orders />} />
						<Route path="*" element={<div>404</div>} />
					</Routes>
				</ModalProvider>
			</MantineProvider>
		</>
	);
};
